"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import Link from "next/link";
import ForestControls from "./ForestControls";
import ForestIntro from "./ForestIntro";
import ForestErrorBoundary from "./ForestErrorBoundary";
import useForestAudio from "@/hooks/useForestAudio";

// ForestScene needs ssr:false because @react-three/fiber's <Canvas>
// can't initialize a WebGL context server-side. The loading fallback
// is now TRANSPARENT (was a dark teal div that bled "green" through
// the doorway) — when ForestScene is still loading, nothing is
// painted underneath the intro, so the wrapper bg shows instead.
const ForestScene = dynamic(() => import("./forest"), {
  ssr: false,
  loading: () => null,
});

const ForestWalk = () => {
  // Dev preview: /forest?finale skips the intro and starts the camera near
  // the end of the walk so the cliffside-workshop reveal can be reviewed
  // without sitting through the full trail.
  const finale =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("finale");

  // Season preset — drives foliage palette, sun angle, fog colours, ground tones.
  const season =
    typeof window !== "undefined"
      ? (new URLSearchParams(window.location.search).get("season") as
          | "summer"
          | "autumn"
          | "winter"
          | "spring"
          | null) ?? "summer"
      : "summer";

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  // ForestScene mounts immediately so its bundle downloads/parses in
  // parallel with the intro. The intro door does NOT swing on a timer —
  // it gates on `sceneReady`, which fires after the WebGL canvas has
  // actually rendered its first frame. This guarantees the user never
  // sees a loading-state void through the doorway when it opens.
  const [introDone, setIntroDone] = useState(finale);
  const [sceneReady, setSceneReady] = useState(false);
  const [introPhase, setIntroPhase] = useState<string>("code");
  const handleIntroComplete = useCallback(() => setIntroDone(true), []);
  const handleSceneReady = useCallback(() => setSceneReady(true), []);

  const audio = useForestAudio();
  const audioStarted = useRef(false);

  const handleSetBrookVolume = useCallback(
    (volume: number) => {
      audio.setBrookVolume(volume);
    },
    [audio]
  );

  // Audio is started ONLY on first user gesture. React onClick on the
  // wrapper div (below) is the primary unlock — React bubbles synthetic
  // events through its tree even when the underlying R3F canvas
  // intercepts the native event. Window-level keydown stays as a
  // fallback for keyboard users.
  const handleUnlock = useCallback(() => {
    if (audioStarted.current) return;
    audioStarted.current = true;
    audio.start();
    // Audio started on user gesture
  }, [audio]);

  useEffect(() => {
    window.addEventListener("keydown", handleUnlock);
    return () => window.removeEventListener("keydown", handleUnlock);
  }, [handleUnlock]);

  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      audio.stop();
    } else {
      setIsPlaying(true);
      audio.start();
    }
  }, [isPlaying, audio]);

  const handleToggleMute = useCallback(() => {
    const next = !isMuted;
    setIsMuted(next);
    audio.setMuted(next);
  }, [isMuted, audio]);

  return (
    <div
      className="flex h-screen w-screen items-center justify-center overflow-hidden bg-[#070710]"
      onPointerDown={handleUnlock}
      onTouchStart={handleUnlock}
    >
      {/* 16:9 letterboxed container — ensures the full width of the
          forest is always visible, with black bars above/below on tall
          screens instead of cropping the sides. */}
      <div className="relative aspect-video w-full max-h-screen">
        {/* Forest 3D scene — now MOUNTS IMMEDIATELY at intro start so
            the bundle downloads + parses during the intro's 7s window.
            Camera animation is gated by `isPlaying && introDone` so the
            walk doesn't start until the doorway is fully revealed. The
            forest scene is visible THROUGH the doorway as the door swings
            open in phase B. */}
        <ForestErrorBoundary>
          <ForestScene
            isPlaying={isPlaying && introDone}
            onReady={handleSceneReady}
            introPhase={finale ? "walk" : introPhase}
            finale={finale}
            season={season}
            onSetBrookVolume={handleSetBrookVolume}
          />
        </ForestErrorBoundary>

        {/* Entry intro — door swings only when sceneReady is true.
            Skipped entirely in the /forest?finale dev preview. */}
        {!finale && (
          <ForestIntro sceneReady={sceneReady} onComplete={handleIntroComplete} onDoorOpen={audio.playDoorCreak} onPhaseChange={setIntroPhase} />
        )}

        {/* Loading screen — covers everything until the room/door have
            actually rendered, so the runes never appear over a blank scene. */}
        <motion.div
          className="pointer-events-none absolute inset-0 z-[200] flex flex-col items-center justify-center bg-[#070710]"
          initial={{ opacity: 1 }}
          animate={{ opacity: sceneReady ? 0 : 1 }}
          transition={{ duration: 0.9, ease: "easeInOut" }}
        >
          <motion.div
            className="h-3 w-3 rounded-full bg-emerald-400"
            animate={{ opacity: [0.25, 1, 0.25], scale: [0.85, 1.15, 0.85] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
          <span className="mt-5 text-[11px] uppercase tracking-[0.35em] text-emerald-300/50 font-[family-name:var(--font-jetbrains-mono)]">
            Entering
          </span>
        </motion.div>

        {/* Back to site link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute left-6 top-6 z-20"
        >
          <Link
            href="/"
            className="flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-4 py-2 text-sm text-white/50 backdrop-blur-md transition-all hover:border-emerald-500/30 hover:text-white/80"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M10 2L4 8l6 6" />
            </svg>
            Back to site
          </Link>
        </motion.div>

        {/* Controls overlay — play/pause + mute only */}
        <ForestControls
          isPlaying={isPlaying}
          isMuted={isMuted}
          onTogglePlay={handleTogglePlay}
          onToggleMute={handleToggleMute}
        />
      </div>
    </div>
  );
};

export default ForestWalk;
